import { Location } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import {
  ButtonComponent,
  FeedbackPopupService,
  LayoutFoldableSectionComponent,
  LayoutGridCellComponent,
  LayoutGridComponent,
  SpinnerComponent
} from '@forgerock/backstage-ui-components';
import { AlertService, TryCatcher, WINDOW } from '@forgerock/backstage-ui-core';
import type { Subscription } from 'rxjs';
import { debounceTime, firstValueFrom, Subject, switchMap, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { EverythingSearchService } from '../../everything-search.service';
import { LayoutHiddenOnMobileComponent } from '../../layout/layout-hidden-on-mobile/layout-hidden-on-mobile.component';
import { LayoutTabContainerComponent } from '../../layout/layout-tab-container/layout-tab-container.component';
import { LayoutTabComponent } from '../../layout/layout-tab/layout-tab.component';
import { AppSearchParamsInt } from '../../model/app-search-params';
import { EverythingSearchResult } from '../../model/everything-search-result';
import { Tab, tabList, tabLookup } from '../../model/tabs';
import { AggregationKeyValuePipe } from '../../pipe/aggregation-key-value.pipe';
import { DidYouMeanComponent } from '../../search-params/did-you-mean/did-you-mean.component';
import { SearchFilterChipsComponent } from '../../search-params/search-filter-chips/search-filter-chips.component';
import {
  SearchFilterEntityTypeComponent
} from '../../search-params/search-filter-entity-type/search-filter-entity-type.component';
import {
  SearchFilterLibraryComponent
} from '../../search-params/search-filter-library/search-filter-library.component';
import {
  SearchFilterProductComponent
} from '../../search-params/search-filter-product/search-filter-product.component';
import { SearchFilterQueryComponent } from '../../search-params/search-filter-query/search-filter-query.component';
import { SearchFilterSourceComponent } from '../../search-params/search-filter-source/search-filter-source.component';
import {
  SearchFilterValueListComponent
} from '../../search-params/search-filter-value-list/search-filter-value-list.component';
import { SearchParamsAdapterService } from '../../search-params/search-params-adapter.service';
import {
  SearchParamsContainerComponent
} from '../../search-params/search-params-container/search-params-container.component';
import { SearchParamsMapperService } from '../../search-params/search-params-mapper.service';
import { SearchParamsSortComponent } from '../../search-params/search-params-sort/search-params-sort.component';
import { SearchHitCountComponent } from '../../search-result/search-hit-count/search-hit-count.component';
import { TabComponent } from '../../tab/tab/tab.component';

@Component({
  selector: 'app-search-page',
  templateUrl: './search-page.component.html',
  styleUrls: [ './search-page.component.scss' ],
  imports: [ SpinnerComponent, SearchParamsContainerComponent, LayoutGridComponent, LayoutGridCellComponent,
    SearchFilterQueryComponent, DidYouMeanComponent, SearchFilterChipsComponent, LayoutHiddenOnMobileComponent,
    LayoutFoldableSectionComponent, SearchFilterSourceComponent, SearchFilterValueListComponent,
    SearchFilterProductComponent, SearchFilterEntityTypeComponent, SearchFilterLibraryComponent,
    LayoutTabContainerComponent, LayoutTabComponent, SearchParamsSortComponent, SearchHitCountComponent,
    TabComponent, AggregationKeyValuePipe, ButtonComponent ]
})
export class SearchPageComponent extends TryCatcher implements OnInit, OnDestroy {
  params: AppSearchParamsInt;
  paramsChange = new EventEmitter<AppSearchParamsInt>();
  tabList = tabList;
  tabLookup = tabLookup;
  tab: Tab;
  search = inject(EverythingSearchService);
  protected alertService = inject(AlertService);
  private sub: Subscription;
  private routeParams = inject(SearchParamsAdapterService);
  private mapper = inject(SearchParamsMapperService);
  private window = inject(WINDOW);
  private location = inject(Location);
  private feedbackPopupService = inject(FeedbackPopupService);

  async ngOnInit(): Promise<void> {
    // trigger search whenever the params change
    this.sub = this.paramsChange
      .pipe(
        tap(() => this.spinning = true),
        debounceTime(200),
        map(params => this.mapper.filterParamsForTab(params))
      )
      .subscribe({
        next: params =>
          this.tryCatch(async () => {
            this.tab = params.tab;
            const esParams = this.mapper.toEverythingSearchParams(params);
            await Promise.allSettled([
              this.routeParams.updateQueryParams(params),
              this.search.withParams(esParams).searchAsync()
            ]);
            this.feedbackPopupService.show();
          })
      });

    // set initial state from route and run search
    this.params = await firstValueFrom(this.routeParams.params);
    this.tab = this.params.tab;
    this.paramsChange.emit(this.params);

    // update params when the pushState changes (back/forward button)
    const locationChange = new Subject<void>();
    locationChange
      .pipe(
        switchMap(() => this.routeParams.params)
      )
      .subscribe(params => {
        this.params = params;
        this.tab = params.tab;
        this.paramsChange.emit(this.params);
      });
    this.location.subscribe(() => locationChange.next());
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  handleInstantSearchResultClicked(result: EverythingSearchResult) {
    this.window.location.assign(result.url);
  }
}
